import React, { useRef, useState, useContext } from 'react'

// Components
import {
  Button,
  ErrorMessage,
  LoadingPlaceholder,
  Modal,
  ModalContext,
  ModalInjectedProps,
} from '@sketch/components'

// Local Components
import UserChip from '../../components/UserChip'
import SignInWithPasswordForm from './SignInWithPasswordForm'

import MfaVerificationCodeModal from 'modules/user/modals/MfaAuthenticationModal'

// Styles
import {
  UserChipContainer,
  Label,
  LoadingErrorWrapper,
} from './SignInWithPasswordModal.styles'
import { useUserProfile } from '@sketch/modules-common'

// Types
export interface OnlySignInWithPasswordModalProps {
  workspaceName: string
  onCompleted?: () => void
}

type SignInWithPasswordModalProps = OnlySignInWithPasswordModalProps &
  ModalInjectedProps

const SignInWithPasswordModal: React.FC<SignInWithPasswordModalProps> = ({
  workspaceName,
  onCompleted,
  hideModal,
}) => {
  const { showModal } = useContext(ModalContext)

  const { data, loading, error } = useUserProfile()

  const submitButtonRef = useRef<HTMLButtonElement>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [disableSubmit, setDisableSubmit] = useState(false)
  const hasError = error || !data?.me

  const title = `Sign in to the “${workspaceName}” Workspace`

  if (loading || hasError) {
    return (
      <Modal title={title} onCancel={hideModal}>
        <LoadingErrorWrapper>
          {!hasError ? (
            <LoadingPlaceholder size="64px" />
          ) : (
            <ErrorMessage.Generic icon={null} />
          )}
        </LoadingErrorWrapper>

        <Modal.Footer>
          <Button
            variant="secondary"
            disabled={isLoading}
            onClick={() => hideModal()}
          >
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    )
  }

  const user = data.me
  const email = user.personalIdentityEmail!

  const isMfaEnabled = user.mfaState === 'ENABLED'

  return (
    <Modal title={title} onCancel={hideModal}>
      <Modal.Body>
        <UserChipContainer>
          <Label>Email</Label>
          <UserChip
            name={user.name!}
            email={email}
            avatarSrc={user.avatar?.small!}
          />
        </UserChipContainer>
        <SignInWithPasswordForm
          submitButtonRef={submitButtonRef}
          email={email}
          onSubmit={() => setIsLoading(true)}
          onCompleted={data => {
            hideModal()

            if (isMfaEnabled && data.signIn.__typename === 'MFACredentials') {
              showModal(MfaVerificationCodeModal, {
                mfaToken: data.signIn.mfaToken,
                onSuccess: onCompleted,
              })
            } else {
              onCompleted?.()
            }
          }}
          onError={() => setIsLoading(false)}
          onDisableSubmit={disabled => setDisableSubmit(disabled)}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="secondary"
          disabled={isLoading}
          onClick={() => hideModal()}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          disabled={isLoading || disableSubmit}
          loading={isLoading}
          onClick={() => submitButtonRef.current?.click()}
        >
          Sign In
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default SignInWithPasswordModal
